import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { toast } from 'react-toastify';
import { flattenQuery } from 'helpers/flattenQuery';
import { extractErrorMessage } from 'helpers/extractErrorMessage';
import { getLocks as fetchLocks, type LockRow, type LocksPage } from 'api/locks/endpoints/getLocks';
import { getAllLocks as fetchAllLocks } from 'api/locks/operations/getAllLocks';
import { deleteLock as deleteLockRequest } from 'api/locks/endpoints/deleteLock';
import type { FilterConfig, ListColumnConfig } from 'api/shared/listConfig';

interface PaginationState {
  totalItems: number;
  totalPages: number;
  currentPage: number;
  pageSize: number;
}

interface LocksState {
  // Every writer stores a boundary-validated list row: the two reads populate
  // it and delete filters it. Creates/updates go straight through the Layer-1
  // postLock/putLock endpoints (the edit dialog) and land here via a refetch,
  // so no looser single-record shape is ever written directly.
  locks: LockRow[];
  pagination: PaginationState;
  allowedZones: string[];
  status: string | null;
  error: string | null;
  config: ListColumnConfig[];
  filtersConfig: FilterConfig[];
  loading: {
    getAllLocks: boolean;
    getLocksPaginated: boolean;
    deleteLock: boolean;
  };
}

const initialState: LocksState = {
  locks: [],
  pagination: {
    totalItems: 0,
    totalPages: 0,
    currentPage: 1,
    pageSize: 2000,
  },
  allowedZones: [],
  status: null,
  error: null,
  config: [],
  filtersConfig: [],
  loading: {
    getAllLocks: false,
    getLocksPaginated: false,
    deleteLock: false,
  },
};

// Every lock, paged to exhaustion — the node-definition editor's lock pickers,
// which need the whole set rather than a page (see api/locks/operations).
export const getAllLocks = createAsyncThunk<LockRow[], void, { rejectValue: string }>('locks/getAllLocks', async (_arg, { rejectWithValue }) => {
  try {
    return [...(await fetchAllLocks())];
  } catch (err) {
    return rejectWithValue(extractErrorMessage(err, 'Failed to fetch locks'));
  }
});

// GET /locks (paginated) — the list page; needs the full page (rows + config).
export const getLocksPaginated = createAsyncThunk<LocksPage, Record<string, unknown>, { rejectValue: string }>(
  'locks/getLocksPaginated',
  async (params, { rejectWithValue }) => {
    try {
      return await fetchLocks(flattenQuery(params));
    } catch (err) {
      return rejectWithValue(extractErrorMessage(err, 'Failed to fetch locks'));
    }
  }
);

export const deleteLock = createAsyncThunk<{ zone_name: string; name: string }, { zone_name: string; name: string }, { rejectValue: string }>(
  'locks/deleteLock',
  async ({ zone_name, name }, { rejectWithValue }) => {
    try {
      await deleteLockRequest(zone_name, name);
      return { zone_name, name };
    } catch (err) {
      return rejectWithValue(extractErrorMessage(err, 'Failed to delete lock'));
    }
  }
);

const locksSlice = createSlice({
  name: 'locks',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      // getAllLocks
      .addCase(getAllLocks.pending, (state) => {
        state.status = 'loading';
        state.error = null;
        state.loading.getAllLocks = true;
      })
      .addCase(getAllLocks.fulfilled, (state, action) => {
        state.locks = action.payload;
        state.status = 'succeeded';
        state.loading.getAllLocks = false;
      })
      .addCase(getAllLocks.rejected, (state, action) => {
        state.status = 'failed';
        state.loading.getAllLocks = false;
        state.error = action.payload ?? action.error.message ?? 'Failed to fetch locks';
      });

    builder
      .addCase(getLocksPaginated.pending, (state) => {
        state.status = 'loading';
        state.error = null;
        state.loading.getLocksPaginated = true;
      })
      .addCase(getLocksPaginated.fulfilled, (state, action) => {
        state.status = 'succeeded';

        const { locks, config, filtersConfig, pagination, allowedZones } = action.payload;

        state.locks = [...locks];
        state.config = [...config];
        state.filtersConfig = [...filtersConfig];
        state.allowedZones = [...allowedZones];

        state.pagination.totalItems = pagination.total_records;
        state.pagination.totalPages = pagination.total_pages;
        state.pagination.pageSize = pagination.limit;
        state.pagination.currentPage = pagination.current_page > pagination.total_pages ? 1 : pagination.current_page;

        state.loading.getLocksPaginated = false;
      })
      .addCase(getLocksPaginated.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload ?? action.error.message ?? 'Failed to fetch locks';
        state.loading.getLocksPaginated = false;
      });

    builder
      // deleteLock
      .addCase(deleteLock.pending, (state) => {
        state.loading.deleteLock = true;
        state.error = null;
      })
      .addCase(deleteLock.fulfilled, (state, action) => {
        const { zone_name, name } = action.payload;
        state.locks = state.locks.filter((l) => !(l.zone_name === zone_name && l.name === name));
        state.status = 'succeeded';
        state.loading.deleteLock = false;
        const toastId = 'data-deleted-lock';
        if (!toast.isActive(toastId)) {
          toast.success(`Lock deleted successfully!`, { toastId });
        }
      })
      .addCase(deleteLock.rejected, (state, action) => {
        state.loading.deleteLock = false;
        state.status = 'failed';
        state.error = action.payload ?? action.error.message ?? 'Failed to delete lock';
        const toastId = 'data-failed-to-delete-lock';
        if (!toast.isActive(toastId)) {
          toast.error(`Failed to delete the lock! ${state.error}`, { toastId });
        }
      });
  },
});

export default locksSlice.reducer;
